/** Small date/time helpers for the chat UI. */

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** "Today" / "Yesterday" / "Mon, 5 Aug" (+ year when not this year) —
 *  used for the day separators between message groups. */
export function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = startOfDay(new Date());
  const that = startOfDay(d);
  const dayMs = 86_400_000;
  if (that === today) return "Today";
  if (that === today - dayMs) return "Yesterday";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/** True when two ISO timestamps fall on different calendar days. */
export function isNewDay(prevIso: string | null, iso: string): boolean {
  if (!prevIso) return true;
  return startOfDay(new Date(prevIso)) !== startOfDay(new Date(iso));
}

/** "14:32" — clock time shown under a bubble. */
export function clockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** Compact "now / 5m / 3h / 2d / Aug 5" for conversation-list rows. */
export function shortAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 45) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

/** Human file size for attachment chips. */
export function fileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Display name for a file attachment, derived from its URL's last path
 *  segment (the upload route preserves the original filename there). */
export function fileNameFromUrl(url: string): string {
  try {
    const seg = url.split("?")[0].split("#")[0].split("/").filter(Boolean).pop() || "file";
    return decodeURIComponent(seg);
  } catch {
    return "file";
  }
}
